import {Component, EventEmitter, OnInit, Output, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {CodaltComponent} from '../../codalt.component';
import {MatFormField, MatInput} from '@angular/material/input';
import {MatIconButton} from '@angular/material/button';
import {Utils} from '../../utils.class';
import {Company} from '../../classes/company';
import {CompanyService} from '../../services/company.service';
import {Routenames} from '../../classes/route-name.enum';
import {MatIcon} from '@angular/material/icon';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {Azureteam} from '../../classes/azureteam';
import {JsonPipe} from '@angular/common';
import {HasFeaturePipe} from '../../pipes/has-feature.pipe';
import {Title} from '@angular/platform-browser';
import {AfasDebtor} from '../../classes/debtor';
import {AfasService} from '../../services/afas.service';
import {NgSelectComponent} from '@ng-select/ng-select';

@Component({
    selector: 'app-edit-companies',
    templateUrl: './company-edit.component.html',
    styleUrls: ['./company-edit.component.scss'],
    imports: [
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatIcon,
        MatIconButton,
        BreadcrumbComponent,
        HasFeaturePipe,
        BreadcrumbComponent,
        JsonPipe,
        NgSelectComponent
    ]
})
export class CompanyEditComponent extends CodaltComponent implements OnInit {
    @Output() updated = new EventEmitter<any>();

    readonly form = signal<FormGroup>(null);

    company?: Company;
    readonly azureTeams = signal<Azureteam[]>(null);
    readonly azureItems = signal(null);

    readonly debtors = signal<AfasDebtor[]>(null);

    constructor(
        private activatedRoute: ActivatedRoute,
        private companyService: CompanyService,
        private afasService: AfasService,
        private confirmDialog: ConfirmDialogService,
        private router: Router,
        private titleService: Title,
    ) {
        super();
        this.titleService.setTitle(`Bedrijven ${this.environment.titleAppend}`);
    }

    ngOnInit(): void {
        this.subscriptions.add(this.activatedRoute.params.subscribe((params: { id }) => {
            if (params.id === 'new') {
                this.createForm(new Company());
            } else {
                this.subscriptions.add(
                    this.companyService.getCompany(params.id).subscribe({
                        next: res => this.createForm(res),
                        error: err => console.error(err)
                    }));
            }
        }));

        this.subscriptions.add(
            this.afasService.getDebtors().subscribe(debtors => {
                this.debtors.set(debtors);
            })
        );
    }

    createForm(company: Company) {
        this.crumbs = [
            {name: 'Bedrijven', route: Routenames.companies},
            {name: `Bedrijf : ${company.name || 'new'}`, route: `${Routenames.companies}/${company.id || 'new'}`}
        ];

        this.company = company?.id ? company : new Company();

        this.form.set(new FormGroup({
            id: new FormControl(company.id),
            name: new FormControl(company.name, Validators.required),
            short: new FormControl(company.short),
            street: new FormControl(company.street),
            number: new FormControl(company.number),
            addition: new FormControl(company.addition),
            city: new FormControl(company.city),
            postal_code: new FormControl(company.postal_code),
            humres: new FormControl(company.humres),
            email: new FormControl(company.email),
            azure_project_id: new FormControl(company.azure_project_id),
            azure_team_id: new FormControl(company.azure_team_id),
            azure_desc: new FormControl(company.azure_desc),
            afas_debtor_id: new FormControl(company.afas_debtor_id),
            is_groundbirds: new FormControl(company.is_groundbirds ?? false),
            is_internal: new FormControl(company.is_internal ?? false),
            updated_at: new FormControl(company.updated_at)
        }));
    }

    loadAzureTeams() {
        this.subscriptions.add(
            this.companyService.getAzureTeams().subscribe((azureTeams) => {
                this.azureTeams.set(azureTeams.value);
            })
        );
    }

    loadAzureItems() {
        this.subscriptions.add(
            this.companyService.getAzureItems(this.company.id).subscribe((items) => {
                this.azureItems.set(items);
            })
        );
    }

    connectAzure(team: Azureteam) {
        this.company.azure_desc = team.name + ' / ' + team.projectName;
        this.company.azure_project_id = team.projectId;
        this.company.azure_team_id = team.id;

        this.subscriptions.add(
            this.companyService.saveCompany(this.company).subscribe((response) => {
                this.createForm(response.data);
            })
        );
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            const company = new Company();
            Object.assign(company, this.form().value);
            this.subscriptions.add(
                this.companyService.saveCompany(company).subscribe({
                    next: () => this.router.navigate([Routenames.companies]),
                    error: err => console.error(err)
                })
            );
        }
    }

    openDelete(event) {
        event.preventDefault();
        event.stopPropagation();

        const company = new Company();
        Object.assign(company, this.form().value);

        this.confirmDialog
            .confirm(
                'Verwijderen',
                `Weet u zeker dat u ${company.name} wilt verwijderen?`,
                'Ja',
                'Nee'
            )
            .then(() => {
                this.companyService.deleteCompany(company.id).then(() => {
                    this.router.navigate([Routenames.companies]);
                });
            })
            .catch(() => {
                console.log('company not deleted');
            });
    }
}
